// @ts-strict-ignore
import {VNode} from 'snabbdom';
import {AttrsSchema, Component, h} from 'panel';
import moment from 'moment';

import {NO_MAX_DATE_VALUE} from 'common/components/calendar/types';
import {InputComponent} from 'common/components/input/index';
import {TextAlign} from 'common/components/input/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {ScrollingCalendar} from 'common/components/scrolling-calendar/index';
import {CalendarDay, ScrollingCalendarDateSelection} from 'common/components/scrolling-calendar/util';
import {BannerSize, BannerTheme, BannerType} from 'common/components/banner/types';
import {
  CALENDAR_DATE_FORMAT,
  MIDNIGHT_TIME_STRING,
  MINUTE_BEFORE_MIDNIGHT_TIME_STRING,
  SHORT_MONTH_CALENDAR_DATE_FORMAT,
  TIMESTAMP_FORMAT,
  Timezone,
} from 'common/constants';
import {QueryContextManager} from 'common/query-context';
import {LegacyReports} from 'common/report/constants';
import {IronContextRegistry} from 'common/report/context-registry';
import {CurrentProject} from 'common/report/model-resources/projects';
import {getReportName} from 'common/report/util';
import {trackUpsellEvent} from 'common/report/util/tracking-helpers';
import {
  combineDateWithTimeFromDate,
  formatDate,
  formatTimeString,
  getCurrentMoment,
  getTime,
  getTimeString,
  isTimeZero,
  MINUTE_BEFORE_MIDNIGHT_TIME,
  parseDate,
  parseDateWithTimeZone,
  parseTime,
  parseTimestamp,
  SUB_DAY_TIME_UNITS,
  ZERO_TIME,
} from 'common/util/date';
import {emitTrackEvent, KEY_VALUES, scrollIntoViewIfNeeded} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {DateRange, DateRangeAttributes} from 'common/widgets/date-range-picker/models/date-range';
import {DATE_RANGE_TYPES} from 'common/widgets/date-range-picker/models/date-range-factory';
import {
  DateRangeType,
  DisplayContext,
  RelativeDate,
  TimeUnit,
  TimeUnitLabelTranslatable,
} from 'common/widgets/date-range-picker/models/types';
import {DateRangePickerApplyEvent} from 'common/widgets/date-range-picker-new/types';

import {LAST_HOURS_MAX, LAST_MINUTES_MAX} from './constants';
import css from './index.inline.styl';
import {template} from './template';
import {
  DatePickerMode,
  generateDateRangeTrackingProps,
  getBetweenDateRangeAttributes,
  getCalendarSelection,
  getDatePickerMode,
  getInTheLastDateRangeAttributes,
  getOnDateRangeAttributes,
  getRelativeOffset,
  getRelativeWindowUnit,
  getRelativeWindowValue,
  getSimilarDateRangeForPickerMode,
  getSinceDateRangeAttributes,
  isMaxHistoryFinite,
} from './util';

interface DatePickerInputState {
  fromDateText: string;
  fromDate: null | moment.Moment;
  fromTimeText: string;
  isAdvancedMenuOpen: boolean;
  isTimePickerEnabled: boolean;
  toDateText: string;
  toDate: null | moment.Moment;
  toTimeText: string;
  relativeInputErrorMsg?: string;
  relativeWindowValue: null | number;
  relativeWindowUnit: TimeUnit;
  relativeOffsetValue: null | number;
  showEndingField: boolean;
}

interface DateRangePickerState extends DatePickerInputState {
  pickerMode: DatePickerMode;
  selectedPresetIdx: number;
  showCustomControls: boolean;
}

interface DateRangePickerAttrs {
  'date-range': Partial<DateRangeAttributes>;
  'date-picker-modes-options': DatePickerMode[];
  'disable-sub-day-granularity-message': string;
  'display-context': DisplayContext;
  'disable-rolling': boolean;
  'from-time-text': string;
  'force-hide-calendar-preview': boolean;
  'force-hide-preset-menu': boolean;
  'hide-upsell': boolean;
  'is-time-picker-enabled': boolean;
  'max-history-days': number;
  'project-as-of-date': string;
  'reduce-height': boolean;
  'single-picker-mode': boolean;
  'to-time-text': string;
  'tracked-use-case': string;
}

export class DateRangePicker extends Component<
  DateRangePickerState,
  DateRangePickerAttrs,
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  // store date range attributes instead of DateRange instances in selectedDateRanges
  // because the appropriate DateRange child class is determined by the "display-context" component attribute,
  // so we want to delay instance construction until necessary, to avoid widespread reconstruction when "display-context" changes.
  private selectedDateRanges: Map<DatePickerMode, DateRangeAttributes> = new Map<DatePickerMode, DateRangeAttributes>();
  private hoverIsoDate: null | string = null;
  private calendarElement: null | ScrollingCalendar = null;
  // set of timestamps for dates that are disabled because they occur after the "project as of date" but occur before today
  // note: we store the numeric timestamp in the Set because it should be quickest to compare, and we have to compare values on every date cell in <mp-scrolling-calendar> on every render
  private projectAsOfDateDisabledTimestamps: Set<number> = new Set<number>();

  // see deferUntilFrameAfterMouseupOrNewFocus()
  private mouseupOrNewFocusListener: null | EventListener = null;

  // whether the "from date" or "to date" input was recently selected
  // this determines whether which date input should be considered "active" whenever a user clicks a calendar date cell even after the input has lost focus
  // note: they are public so tests can access them
  public fromDateInputRecentlySelected = false;
  public toDateInputRecentlySelected = false;

  static get attrsSchema(): AttrsSchema<DateRangePickerAttrs> {
    return {
      ...super.attrsSchema,
      'date-range': {type: `json`, default: null, description: `Attributes of a valid DateRange instance`},
      'disable-sub-day-granularity-message': {type: `string`, default: ``},
      'disable-rolling': `boolean`,
      'display-context': {type: `string`, default: DisplayContext.FullLength},
      'from-time-text': {
        type: `string`,
        default: ``,
        description: `if set the initial from time text will be filled in with this value.`,
      },
      'hide-upsell': {
        type: `boolean`,
        description: `Hide upsell banner`,
      },
      'is-time-picker-enabled': {type: `boolean`, description: `Show time selection in date/time picker.`},
      'max-history-days': {type: `number`, default: 0, description: `max days time menu can query into data history`},
      'single-picker-mode': {type: `boolean`, description: `if set, switching between date picker modes is disabled`},
      'date-picker-modes-options': {
        type: `json`,
        description: `if set, overrides the list of supported date picker modes`,
        default: [DatePickerMode.Between, DatePickerMode.Since, DatePickerMode.InTheLast],
      },
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'to-time-text': {
        type: `string`,
        default: ``,
        description: `if set the initial to time text will be filled in with this value.`,
      },
      'tracked-use-case': {
        type: `string`,
        description: `For tracking purposes only. Unlike "display-context" which represents a unique configuration that may be reused in multiple places, this attribute directly identifies semantic use cases for distinguishing which parts of the product this component is used in.`,
      },
      'force-hide-preset-menu': {
        type: `boolean`,
        description: `Always hide the side menu that contains a list of presets`,
      },
      'force-hide-calendar-preview': `boolean`,
      'reduce-height': {
        type: `boolean`,
        description: `Reduce menu height so bottom actions bar is not hidden for short viewports. Remove once we have a better solution https://mixpanel.slack.com/archives/CH1J02672/p1720482268238909`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      contexts: [`currentProject` as const, `queryContextManager` as const, `i18n` as const],
      defaultContexts: {
        queryContextManager: new QueryContextManager(),
      },
      defaultState: {
        pickerMode: DatePickerMode.Between,
        selectedPresetIdx: -1,
        showCustomControls: false,
        showEndingField: false,

        // input state
        fromDateText: ``,
        fromDate: null,
        fromTimeText: MIDNIGHT_TIME_STRING,
        isAdvancedMenuOpen: false,
        isTimePickerEnabled: false,
        toDateText: ``,
        toDate: null,
        toTimeText: MINUTE_BEFORE_MIDNIGHT_TIME_STRING,
        relativeWindowValue: null,
        relativeWindowUnit: TimeUnit.Day,
        relativeOffsetValue: null,
      },

      helpers: {
        ...super.config.helpers,
        DatePickerMode,
        TextAlign,
        BannerSize,
        BannerTheme,
        BannerType,

        shouldHideCalendarPreview: () =>
          this.attr(`force-hide-calendar-preview`) ||
          (this.state.pickerMode === DatePickerMode.InTheLast &&
            SUB_DAY_TIME_UNITS.includes(this.state.relativeWindowUnit)),
        canUseRolling: () => {
          if (this.attr(`disable-rolling`)) {
            return false;
          }

          const dateRangeClass = this.getDateRangeClass();
          return new dateRangeClass().canUseRolling();
        },
        getPresetMenuSections: (): Section[] => {
          const dateRangeClass = this.getDateRangeClass();
          const presetItems: SectionItem[] = dateRangeClass.getPresetOptions().map((option, idx: number) => {
            const dateRange = new dateRangeClass(option.value);
            if (!dateRange.isValid()) {
              throw new Error(`"${option.label}" preset is invalid`);
            }

            const maxHistoryDays = this.attr(`max-history-days`);
            const isLimited = isMaxHistoryFinite(maxHistoryDays) && !dateRange.isValid(maxHistoryDays);
            return {
              isSelected: this.state.selectedPresetIdx === idx,
              label: option.label,
              value: option.value,
              isDisabled: isLimited,
              tooltip: isLimited
                ? {
                    text: `Your current plan supports ${maxHistoryDays} days of data. Upgrade your plan to query more data.`,
                  }
                : undefined,
            };
          });
          return [{items: presetItems.filter(Boolean)}];
        },
        getPickerModeToggleOptions: (): SectionItem[] => {
          const dateRangeClass = this.getDateRangeClass();
          const validRangeTypes = new Set<DateRangeType>(new dateRangeClass().getDateRangeTypeOptions());

          const pickerModeLabels: Array<[DatePickerMode, string]> = [
            [DatePickerMode.Between, this.t(`reports.date-range.custom.mode.between`)],
            [DatePickerMode.On, this.t(`reports.date-range.custom.mode.on`)],
            [DatePickerMode.Since, this.t(`reports.date-range.custom.mode.since`)],
            [DatePickerMode.InTheLast, this.t(`reports.date-range.custom.mode.in-the-last`)],
          ];
          const pickerModeItems: SectionItem[] = pickerModeLabels
            .filter(([pickerMode, _label]: [DatePickerMode, string]) => {
              if (!this.attr(`date-picker-modes-options`).includes(pickerMode)) {
                return false;
              }

              switch (pickerMode) {
                case DatePickerMode.InTheLast: {
                  return validRangeTypes.has(DateRangeType.RelativeAfter);
                }
                case DatePickerMode.Between: {
                  return validRangeTypes.has(DateRangeType.Between);
                }
                case DatePickerMode.Since: {
                  return validRangeTypes.has(DateRangeType.Since);
                }
                case DatePickerMode.On: {
                  return validRangeTypes.has(DateRangeType.On);
                }
                default: {
                  const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
                  throw new Error(`unknown picker mode ${unreachable}`);
                }
              }
            })
            .map(([pickerMode, label]: [DatePickerMode, string]) => ({label, value: pickerMode}));
          return pickerModeItems;
        },
        getTodayIso: (): string => getCurrentMoment({localized: true}).toISOString(),
        getTodayDateString: (): string => {
          const queryTimeZone = this.getCurrentProject()?.timezone;
          return getCurrentMoment({timezone: queryTimeZone}).format(TIMESTAMP_FORMAT);
        },
        getMaximumDateIso: (): string => {
          const maxHistoryDays = this.attr(`max-history-days`);
          return isMaxHistoryFinite(maxHistoryDays)
            ? this.getProjectAsOfDate().subtract(maxHistoryDays, `day`).toISOString()
            : ``;
        },
        getDisabledStartIso: (): string => {
          const dateRange = new (this.getDateRangeClass())();
          const queryTimeZone = this.getCurrentProject()?.timezone;
          const projectAsOfDate = this.getProjectAsOfDate(queryTimeZone).format(TIMESTAMP_FORMAT);
          return dateRange.getCalendarMaxDate() === NO_MAX_DATE_VALUE ? `` : projectAsOfDate;
        },
        getTimeUnitLabel: (unit: TimeUnit, value: number): string => {
          return this.print(TimeUnitLabelTranslatable[unit](value)).toLowerCase();
        },
        getStartOfWeek: (): number => {
          const weekStartDaySetting = this.getCurrentProject()?.timePeriodSettings?.weekStartDay ?? 0;
          // adjust Setting's weekday (which starts on Monday = 0) to <mp-scrolling-calendar>'s weekday (which starts on Sunday = 0)
          return (weekStartDaySetting + 1) % 7;
        },

        // --- calendar
        getCalendarSelection: (): null | ScrollingCalendarDateSelection => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          const queryTimeZone = this.getCurrentProject()?.timezone;

          if (selectedDateRange) {
            return getCalendarSelection(selectedDateRange, this.getProjectAsOfDate(queryTimeZone), queryTimeZone);
          }
          return null;
        },
        getCalendarPreviewSelection: (): null | ScrollingCalendarDateSelection => {
          if (this.shouldShowCalendarPreviewSelection(this.state.pickerMode)) {
            const queryTimeZone = this.getCurrentProject()?.timezone;

            let hoverDate = this.hoverIsoDate && moment(this.hoverIsoDate, moment.ISO_8601);
            if (this.hoverIsoDate) {
              hoverDate = parseDateWithTimeZone(hoverDate.format(CALENDAR_DATE_FORMAT), queryTimeZone);
            }
            const hoverDateRange = this.getCalendarHoverDateRange(this.state.pickerMode, hoverDate);
            if (hoverDateRange) {
              const selection = getCalendarSelection(
                hoverDateRange,
                this.getProjectAsOfDate(queryTimeZone),
                queryTimeZone,
              );
              if (selection) {
                // This is a visual hack that hides the blue highlight circle that overlaps the hover circle.
                //
                // Without this hack, <mp-scrolling-calendar> would render a hover circle on top of the blue highlight circle,
                // so moving your mouse rapidly across dates can sometimes show both the hover circle and the blue highlight circle
                // visually blended together as the browser tries to render one after the other.
                const overrideHighlightStart = hoverDate?.isSame(moment(selection.start, moment.ISO_8601), `day`);
                const overrideHighlightEnd = hoverDate?.isSame(moment(selection.end, moment.ISO_8601), `day`);
                return {
                  ...selection,
                  ...(overrideHighlightStart && {highlightStart: false}),
                  ...(overrideHighlightEnd && {highlightEnd: false}),
                };
              }
            }
          }
          return null;
        },
        handleCalendarDateClick: (ev: CustomEvent): void => {
          const queryTimeZone = this.getCurrentProject()?.timezone;
          const {fromDate, pickerMode, toDate} = this.state;

          let clickedDate = moment(ev.detail.date, moment.ISO_8601);
          let calendarFromDate: null | moment.Moment = null;
          let calendarToDate: null | moment.Moment = null;
          if (ev.detail.date) {
            clickedDate = parseDateWithTimeZone(clickedDate.format(TIMESTAMP_FORMAT), queryTimeZone);

            if (!fromDate?.isValid() && toDate?.isValid()) {
              calendarFromDate = clickedDate;
              calendarToDate = toDate;
            } else if (fromDate?.isValid() && !toDate?.isValid() && pickerMode === DatePickerMode.Between) {
              if (clickedDate.isSameOrBefore(fromDate, `day`)) {
                // The clicked date is before fromDate, so move the current fromDate to
                // 'calendarToDate' to be set in to date and clickedDate will be the
                // 'calendarFromDate'.
                calendarFromDate = clickedDate;
                calendarToDate = fromDate;
              } else {
                calendarFromDate = fromDate;
                calendarToDate = clickedDate;
              }
            } else {
              calendarFromDate = clickedDate;
            }

            if (this.state.isTimePickerEnabled) {
              calendarFromDate = calendarFromDate?.isValid()
                ? combineDateWithTimeFromDate(
                    calendarFromDate,
                    this.state.fromDate ?? this.getTimeOrDefault(this.state.fromTimeText, MINUTE_BEFORE_MIDNIGHT_TIME),
                  )
                : calendarFromDate;
              calendarToDate =
                calendarToDate?.isValid() && pickerMode === DatePickerMode.Between
                  ? combineDateWithTimeFromDate(
                      calendarToDate,
                      this.state.toDate ?? this.getTimeOrDefault(this.state.toTimeText),
                    )
                  : calendarToDate;
            }
          }

          if (pickerMode === DatePickerMode.Between) {
            const updateDateState = (from: null | moment.Moment, to: null | moment.Moment) => {
              this.update({
                fromDateText: from ? from.format(SHORT_MONTH_CALENDAR_DATE_FORMAT) : ``,
                fromDate: from,
                toDateText: to ? to.format(SHORT_MONTH_CALENDAR_DATE_FORMAT) : ``,
                toDate: to,
                selectedPresetIdx: -1,
              });
            };
            if (this.fromDateInputRecentlySelected) {
              const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
                ...this.state,
                fromDate: clickedDate,
              });
              this.setSelectedDateRange(this.state.pickerMode, replacement);
              updateDateState(
                replacement.from ? parseTimestamp(replacement.from as string) : clickedDate,
                replacement?.to ? parseTimestamp(replacement.to as string) : null,
              );
              return;
            }
            if (this.toDateInputRecentlySelected) {
              const replacement = this.getDateRangeFromInputs(pickerMode, {
                ...this.state,
                toDate: clickedDate,
              });
              this.setSelectedDateRange(pickerMode, replacement);
              updateDateState(
                replacement?.from ? parseTimestamp(replacement.from as string) : null,
                replacement?.to ? parseTimestamp(replacement.to as string) : clickedDate,
              );
              return;
            }
          }

          let hoverDateRange;
          if (calendarFromDate?.isValid() && calendarToDate?.isValid()) {
            const dateRangeClass = this.getDateRangeClass();
            const hoverDateRangeAttrs = this.getDateRangeFromInputs(this.state.pickerMode, {
              ...this.state,
              fromDate: calendarFromDate,
              toDate: calendarToDate,
            });
            hoverDateRange = new dateRangeClass(hoverDateRangeAttrs);
          } else {
            hoverDateRange = this.getCalendarHoverDateRange(pickerMode, calendarFromDate);
          }

          if (hoverDateRange) {
            this.setSelectedDateRange(pickerMode, hoverDateRange);
            this.update({
              ...this.getInputStateFromDateRange(this.getSelectedDateRange(pickerMode)),
              selectedPresetIdx: -1,
            });
          } else if (pickerMode === DatePickerMode.Between) {
            const clickedDateWithTime = combineDateWithTimeFromDate(
              clickedDate,
              this.state.fromDate ?? this.getTimeOrDefault(this.state.fromTimeText),
            );
            this.setSelectedDateRange(pickerMode, null);
            this.update({
              fromDateText: clickedDate.format(SHORT_MONTH_CALENDAR_DATE_FORMAT),
              fromDate: clickedDateWithTime,
              toDateText: ``,
              toDate: null,
              selectedPresetIdx: -1,
            });
          }
        },
        handleCalendarDateMouseEnter: (ev: CustomEvent): void => {
          this.hoverIsoDate = ev.detail.date;
          this.update();
        },
        handleCalendarDateMouseLeave: (ev: CustomEvent): void => {
          if (this.hoverIsoDate === ev.detail.date) {
            this.hoverIsoDate = null;
            this.update();
          }
        },
        handleCalendarElementInsert: (vnode) => {
          this.calendarElement = vnode.elm;
        },
        handleCalendarElementRemove: (_, removeCallback) => {
          this.calendarElement = null;
          removeCallback();
        },

        // --- window unit
        getWindowUnitMenuSections: (): Section[] => {
          const dateRangeClass = this.getDateRangeClass();
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);

          const items = (selectedDateRange || new dateRangeClass())
            .getTimeUnitOptions()
            .filter((value: TimeUnit) => {
              // Only show sub-day time units if the time picker is enabled.
              return this.state.isTimePickerEnabled || !SUB_DAY_TIME_UNITS.includes(value);
            })
            .map(
              (value: TimeUnit): SectionItem => ({
                isSelected: this.state.relativeWindowUnit === value,
                label: this.helpers.getTimeUnitLabel(value, this.state.relativeWindowValue || 0),
                value,
              }),
            );

          return [{items}];
        },
        handleInTheLastWindowUnitChange: (ev: CustomEvent): void => {
          const windowUnit: TimeUnit = ev.detail.item.value;
          const errorMsg: string = this.getInTheLastWindowErrorMessage(this.state.relativeWindowValue, windowUnit);
          // enforce limits.
          this.update({
            relativeInputErrorMsg: errorMsg,
          });

          const additionalInputChanges: Partial<DatePickerInputState> = {};
          // fallback to 1 for window value because zero-value windows are technically always in "Days" (because they are converted to "today")
          if (windowUnit !== TimeUnit.Day) {
            additionalInputChanges.relativeWindowValue = this.state.relativeWindowValue || 1;
          }

          // update selected date immediately
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            ...additionalInputChanges,
            relativeWindowUnit: windowUnit,
          });
          this.setSelectedDateRange(this.state.pickerMode, replacement);

          // refresh input
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          this.update({
            ...(selectedDateRange && this.getInputStateFromDateRange(selectedDateRange)),

            // make sure user-selected window unit overrides the state from additionalInputState because
            // zero-value windows are technically always in "Days" (because they are converted to "today"), but we want to show the unit that the user just selected
            relativeWindowUnit: windowUnit,
            selectedPresetIdx: -1,
          });
          if (selectedDateRange) {
            const selection = getCalendarSelection(selectedDateRange, this.getProjectAsOfDate());
            if (selection) {
              // selection can be null if selectedDateRange is invalid (eg. for absurdly large month windows that reaches before the epoch)
              this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
            }
          }
        },

        // --- window value
        getSelectedWindowValue: (): number => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          if (selectedDateRange) {
            return getRelativeWindowValue(selectedDateRange, this.getProjectAsOfDate());
          }
          return null;
        },
        handleInTheLastWindowValueInput: (ev: InputEvent): void => {
          const target: any = ev.target;
          const inputValue: number = parseInt(target.value, 10);
          const errorMsg: string = this.getInTheLastWindowErrorMessage(inputValue, this.state.relativeWindowUnit);
          this.update({relativeInputErrorMsg: errorMsg});

          if (Number.isInteger(inputValue)) {
            // update selected date immediately
            const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
              ...this.state,
              relativeWindowValue: inputValue,
            });
            this.setSelectedDateRange(this.state.pickerMode, replacement);

            // refresh input with valid value
            const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
            this.update({
              relativeInputErrorMsg: errorMsg,
              relativeWindowValue: inputValue,
              selectedPresetIdx: -1,
              ...(selectedDateRange && this.getInputStateFromDateRange(selectedDateRange)),
            });
            if (selectedDateRange) {
              const selection = getCalendarSelection(selectedDateRange, this.getProjectAsOfDate());
              if (selection) {
                // selection can be null if selectedDateRange is invalid (eg. for absurdly large month windows that reaches before the epoch)
                this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
              }
            }
          }
        },

        // --- window offset
        getSelectedOffset: (): RelativeDate => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          if (selectedDateRange) {
            return getRelativeOffset(selectedDateRange, this.getProjectAsOfDate());
          }
          return {unit: TimeUnit.Day, value: 0};
        },
        handleInTheLastOffsetValueInput: (ev: InputEvent): void => {
          const target: any = ev.target;
          const inputValue: number = parseInt(target.value, 10);

          // update selected date immediately
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            relativeOffsetValue: inputValue,
          });
          this.setSelectedDateRange(this.state.pickerMode, replacement);

          // refresh input with valid value
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          this.update({
            relativeOffsetValue: inputValue,
            selectedPresetIdx: -1,
            ...(selectedDateRange && this.getInputStateFromDateRange(selectedDateRange)),
          });
          if (selectedDateRange) {
            const queryTimeZone = this.getCurrentProject()?.timezone;

            const selection = getCalendarSelection(
              selectedDateRange,
              this.getProjectAsOfDate(queryTimeZone),
              queryTimeZone,
            );
            if (selection) {
              // selection can be null if selectedDateRange is invalid (eg. for absurdly large month windows that reaches before the epoch)
              this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
            }
          }
        },

        handleInTheLastWindowAndOffsetValueBlur: (): void => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          if (selectedDateRange) {
            this.update(this.getInputStateFromDateRange(selectedDateRange));
          }
        },

        handleShowEndingClick: (): void => {
          this.update({showEndingField: true});
          window.requestAnimationFrame(() => {
            const input: InputComponent = this.el.querySelector(`mp-input.ending-relative-offset`);
            input.focus();
          });
        },
        handleHideEndingClick: (): void => {
          // update selected date immediately
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            relativeOffsetValue: 0,
          });
          this.setSelectedDateRange(this.state.pickerMode, replacement);

          // refresh input with valid value
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          this.update({
            relativeOffsetValue: 0,
            selectedPresetIdx: -1,
            showEndingField: false,
            ...(selectedDateRange && this.getInputStateFromDateRange(selectedDateRange)),
          });
          if (selectedDateRange) {
            const selection = getCalendarSelection(selectedDateRange, this.getProjectAsOfDate());
            if (selection) {
              // selection can be null if selectedDateRange is invalid (eg. for absurdly large month windows that reaches before the epoch)
              this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
            }
          }
        },
        addTimeToDates: (): void => {
          const parsedFromTime: moment.Moment = parseTime(this.state.fromTimeText);
          const parsedFromTimeDuration = moment.duration({
            hours: parsedFromTime.hours(),
            minutes: parsedFromTime.minutes(),
          });
          const replacementFromDateUpdated = moment(this.state.fromDate).startOf(`day`).add(parsedFromTimeDuration);

          const parsedToTime: moment.Moment = parseTime(this.state.toTimeText);
          const parsedToTimeDuration = moment.duration({
            hours: parsedToTime.hours(),
            minutes: parsedToTime.minutes(),
          });
          const replacementToDateUpdated = moment(this.state.toDate).startOf(`day`).add(parsedToTimeDuration);

          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            fromDate: replacementFromDateUpdated,
            toDate: replacementToDateUpdated,
          });
          this.setSelectedDateRange(this.state.pickerMode, replacement);
          this.update({
            fromDate: replacementFromDateUpdated,
            toDate: replacementToDateUpdated,
          });
        },
        clearTimeFromDates: (): void => {
          // When the time picker is disabled clear the time from the dates used for querying,
          // but preserve the text, so that if it's re-enabled the previous state is saved.
          const fromDateTimeCleared = this.state.fromDate?.startOf(`day`);
          const toDateTimeCleared = this.state.toDate?.startOf(`day`);
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            fromDate: fromDateTimeCleared,
            isTimePickerEnabled: false,
            toDate: toDateTimeCleared,
          });
          this.setSelectedDateRange(this.state.pickerMode, replacement);
          this.update({
            fromDate: fromDateTimeCleared,
            toDate: toDateTimeCleared,
          });
        },
        // --- absolute "from" and "to" dates
        handleFromDateInput: (ev: InputEvent): void => {
          const target: any = ev.target;

          const fromDateText: string = target.value;
          const parsedDate: null | Date = parseDate(fromDateText);

          if (parsedDate) {
            const parsedDateWithTime: moment.Moment = combineDateWithTimeFromDate(
              moment(parsedDate),
              this.state.fromDate ?? this.getTimeOrDefault(this.state.fromTimeText),
            );

            const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
              ...this.state,
              fromDate: parsedDateWithTime,
            });
            this.setSelectedDateRange(this.state.pickerMode, replacement);
            this.update({
              fromDateText,
              fromDate: parsedDateWithTime,
              selectedPresetIdx: -1,
            });
          } else {
            this.update({
              fromDateText,
              fromDate: null,
              selectedPresetIdx: -1,
            });
          }

          const selection = this.helpers.getCalendarSelection();
          if (selection) {
            this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
          } else if (parsedDate) {
            this.calendarElement.scrollDateIntoView(moment(parsedDate), /* preferNearTop */ true);
          }
        },
        handleFromTimeInput: (ev: InputEvent): void => {
          const target: any = ev.target;

          const fromTimeText: string = target.value;
          const parsedTime: moment.Moment = parseTime(fromTimeText);
          const parsedTimeDuration = moment.duration({hours: parsedTime.hours(), minutes: parsedTime.minutes()});
          const replacementFromDateUpdated = moment(this.state.fromDate).startOf(`day`).add(parsedTimeDuration);
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            fromDate: replacementFromDateUpdated,
            fromTimeText,
          });
          this.setSelectedDateRange(this.state.pickerMode, replacement);
          this.update({
            fromTimeText,
            fromDate: replacementFromDateUpdated,
            selectedPresetIdx: -1,
          });
        },
        handleToDateInput: (ev: InputEvent): void => {
          const target: any = ev.target;

          const toDateText: string = target.value;
          const parsedDate: null | Date = parseDate(toDateText);
          if (parsedDate) {
            let toDateTime = ZERO_TIME;
            if (this.state.isTimePickerEnabled) {
              toDateTime =
                this.state.toDate ?? this.getTimeOrDefault(this.state.toTimeText, MINUTE_BEFORE_MIDNIGHT_TIME);
            }
            const parsedDateWithTime: moment.Moment = combineDateWithTimeFromDate(moment(parsedDate), toDateTime);
            const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
              ...this.state,
              toDate: parsedDateWithTime,
            });
            this.setSelectedDateRange(this.state.pickerMode, replacement);
            this.update({
              toDateText,
              toDate: parsedDateWithTime,
              selectedPresetIdx: -1,
            });
          } else {
            this.update({
              toDateText,
              toDate: null,
              selectedPresetIdx: -1,
            });
          }

          const selection = this.helpers.getCalendarSelection();
          if (selection) {
            this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ false);
          } else if (parsedDate) {
            this.calendarElement.scrollDateIntoView(moment(parsedDate), /* preferNearTop */ false);
          }
        },
        handleToTimeInput: (ev: InputEvent): void => {
          const target: any = ev.target;

          const toTimeText: string = target.value;
          const parsedTime: moment.Moment = parseTime(toTimeText);
          const parsedTimeDuration = moment.duration({hours: parsedTime.hours(), minutes: parsedTime.minutes()});
          const replacementToDateUpdated = moment(this.state.toDate).startOf(`day`).add(parsedTimeDuration);
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            toDate: replacementToDateUpdated,
            toTimeText,
          });
          this.setSelectedDateRange(this.state.pickerMode, replacement);
          this.update({
            toTimeText,
            toDate: replacementToDateUpdated,
            selectedPresetIdx: -1,
          });
        },
        handleFromDateArrowKeys: (ev: KeyboardEvent): void => {
          if (this.state.fromDate) {
            const newDate: null | moment.Moment = (() => {
              switch (ev.key) {
                case KEY_VALUES.ARROW_UP: {
                  return this.state.fromDate.clone().add(1, `day`);
                }
                case KEY_VALUES.ARROW_DOWN: {
                  return this.state.fromDate.clone().subtract(1, `day`);
                }
              }
              return null;
            })();

            if (newDate) {
              const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
                ...this.state,
                fromDate: newDate,
              });
              this.setSelectedDateRange(this.state.pickerMode, replacement);
              this.update({
                fromDateText: formatDate(newDate.toDate()),
                fromDate: newDate,
                selectedPresetIdx: -1,
              });

              const selection = this.helpers.getCalendarSelection();
              if (selection) {
                this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
              } else {
                this.calendarElement.scrollDateIntoView(newDate, /* preferNearTop */ true);
              }
              ev.preventDefault();
            }
          }
        },
        handleFromTimeArrowKeys: (ev: KeyboardEvent): void => {
          const parsedTime: moment.Moment = parseTime(this.state.fromTimeText);

          const newTime: null | moment.Moment = (() => {
            switch (ev.key) {
              case KEY_VALUES.ARROW_UP: {
                return parsedTime.add(1, `minute`);
              }
              case KEY_VALUES.ARROW_DOWN: {
                return parsedTime.subtract(1, `minute`);
              }
            }
            return null;
          })();

          if (!newTime) {
            return;
          }

          const newTimeDuration = moment.duration({hours: newTime.hours(), minutes: newTime.minutes()});
          const replacementFromDateUpdated = moment(this.state.fromDate).startOf(`day`).add(newTimeDuration);
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            fromDate: replacementFromDateUpdated,
          });
          this.setSelectedDateRange(this.state.pickerMode, replacement);
          this.update({
            fromTimeText: getTimeString(replacementFromDateUpdated),
            fromDate: replacementFromDateUpdated,
            selectedPresetIdx: -1,
          });
        },
        handleToDateArrowKeys: (ev: KeyboardEvent): void => {
          if (this.state.toDate) {
            const newDate: null | moment.Moment = (() => {
              switch (ev.key) {
                case KEY_VALUES.ARROW_UP: {
                  return this.state.toDate.clone().add(1, `day`);
                }
                case KEY_VALUES.ARROW_DOWN: {
                  return this.state.toDate.clone().subtract(1, `day`);
                }
              }
              return null;
            })();

            if (newDate) {
              const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
                ...this.state,
                toDate: newDate,
              });
              this.setSelectedDateRange(this.state.pickerMode, replacement);
              this.update({
                toDateText: formatDate(newDate.toDate()),
                toDate: newDate,
                selectedPresetIdx: -1,
              });

              const selection = this.helpers.getCalendarSelection();
              if (selection) {
                this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ false);
              } else {
                this.calendarElement.scrollDateIntoView(newDate, /* preferNearTop */ false);
              }
              ev.preventDefault();
            }
          }
        },
        handleToTimeArrowKeys: (ev: KeyboardEvent): void => {
          const parsedTime: moment.Moment = parseTime(this.state.toTimeText);

          const newTime: null | moment.Moment = (() => {
            switch (ev.key) {
              case KEY_VALUES.ARROW_UP: {
                return parsedTime.add(1, `minute`);
              }
              case KEY_VALUES.ARROW_DOWN: {
                return parsedTime.subtract(1, `minute`);
              }
            }
            return null;
          })();

          if (!newTime) {
            return;
          }

          const newTimeDuration = moment.duration({hours: newTime.hours(), minutes: newTime.minutes()});
          const replacementToDateUpdated = moment(this.state.toDate).startOf(`day`).add(newTimeDuration);
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            toDate: replacementToDateUpdated,
          });
          this.setSelectedDateRange(this.state.pickerMode, replacement);
          this.update({
            toTimeText: getTimeString(replacementToDateUpdated),
            toDate: replacementToDateUpdated,
            selectedPresetIdx: -1,
          });
        },
        // --- presets and modes
        handleClickPreset: (ev: CustomEvent) => {
          const item: SectionItem = ev.detail.item;
          const dateRangeClass = this.getDateRangeClass();
          const selectedPreset = new dateRangeClass(item.value);
          this.dispatchEvent(new CustomEvent(`apply`, {detail: {dateRange: selectedPreset}}));

          this.track(`[mp-query-builder] Select date preset`, {
            ...this.getDateRangeTrackingProps(selectedPreset),
            'Preset name': item.label,
            'Preset source': `mixpanel`,
          });
        },
        handleClickCustom: () => {
          this.update({
            showCustomControls: true,
          });
        },
        handleClickPickerMode: (ev: CustomEvent) => {
          const newPickerMode: DatePickerMode = ev.detail.selected;
          const oldPickerMode = this.state.pickerMode;

          if (this.state.selectedPresetIdx > -1 || newPickerMode !== oldPickerMode) {
            const oldSelectedDateRange = this.getSelectedDateRange(oldPickerMode);
            const queryTimeZone = this.getCurrentProject()?.timezone;
            let similarDate =
              oldSelectedDateRange &&
              getSimilarDateRangeForPickerMode(
                oldSelectedDateRange,
                this.getProjectAsOfDate(queryTimeZone),
                newPickerMode,
                queryTimeZone,
                {
                  fromTime: parseTime(this.state.fromTimeText),
                  toTime: parseTime(this.state.toTimeText),
                },
              );

            if (similarDate) {
              if (this.state.isTimePickerEnabled) {
                if (similarDate.from && parseTimestamp(similarDate.from as string).isValid()) {
                  similarDate = {
                    ...similarDate,
                    from: combineDateWithTimeFromDate(
                      parseTimestamp(similarDate.from as string),
                      parseTime(this.state.fromTimeText),
                    ).format(TIMESTAMP_FORMAT),
                  };
                }
                if (similarDate.to) {
                  similarDate = {
                    ...similarDate,
                    to: combineDateWithTimeFromDate(
                      parseTimestamp(similarDate.to as string),
                      parseTime(this.state.toTimeText),
                    ).format(TIMESTAMP_FORMAT),
                  };
                }
              }
              this.setSelectedDateRange(newPickerMode, similarDate);
            }

            const newSelectedDateRange = this.getSelectedDateRange(newPickerMode);
            this.update({
              pickerMode: newPickerMode,
              // even if the last selected DateRange for this range type is a preset,
              // deselect presets because the user just explicitly clicked a range type
              selectedPresetIdx: -1,
              ...(newSelectedDateRange && this.getInputStateFromDateRange(newSelectedDateRange)),
            });
          }
        },
        getQueryMaxDays: () => {
          return this.getContext(`queryContextManager`)?.getMaxQueryDays();
        },
        shouldShowQueryDaysLimit: (): boolean => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);

          if (Boolean(selectedDateRange?.isValid()) === false) {
            return false;
          }

          const maxQueryDays = this.helpers.getQueryMaxDays();
          const queryTimeZone = this.getCurrentProject()?.timezone;

          let dateRangeIntervalDays = selectedDateRange.getIntervalDays(
            this.getProjectAsOfDate(queryTimeZone).format(CALENDAR_DATE_FORMAT),
          );

          // include last day of absolute day range
          if (selectedDateRange.isRelativeWindow() === false) {
            dateRangeIntervalDays += 1;
          }

          return Boolean(selectedDateRange?.isValid() && maxQueryDays && dateRangeIntervalDays > maxQueryDays);
        },
        // --- exceeds max history days
        exceedsMaxHistoryDays: (): boolean => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          if (selectedDateRange) {
            const maxHistoryDays = this.attr(`max-history-days`);
            return isMaxHistoryFinite(maxHistoryDays) && !selectedDateRange.isValid(maxHistoryDays);
          }
          return false;
        },
        // --- max history banner
        shouldShowMaxHistoryBanner: (): boolean => {
          if (this.attr(`hide-upsell`)) {
            return this.helpers.exceedsMaxHistoryDays();
          }
          return false;
        },
        // --- upsell
        shouldShowUpsell: (): boolean => {
          if (!this.attr(`hide-upsell`)) {
            return this.helpers.exceedsMaxHistoryDays();
          }
          return false;
        },
        handleUpsellBannerInsert: (): void => {
          trackUpsellEvent(`View Upsell`, `date_picker`);
        },
        handleUpsellClick: (): void => {
          trackUpsellEvent(`Click Upsell`, `date_picker`);
        },

        // --- action bar
        handleAdvancedClick: (): void => {
          this.update({isAdvancedMenuOpen: true});
        },
        handleCancelClick: (): void => {
          this.dispatchEvent(new CustomEvent(`cancel`));
        },
        canApply: (): boolean => {
          if (this.state.pickerMode === DatePickerMode.InTheLast && this.state.relativeInputErrorMsg) {
            return false;
          }

          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          const queryTimeZone = this.getCurrentProject()?.timezone;

          if (selectedDateRange?.isValid(this.attr(`max-history-days`)) && this.isTimeRangeValid()) {
            const {from, to} = selectedDateRange.getCalendarDatesAsOf(
              this.getProjectAsOfDate(queryTimeZone).format(CALENDAR_DATE_FORMAT),
            );
            let fromDate = moment(parseDate(from));
            let toDate = moment(parseDate(to));
            // returns `now()` datetime (YYYY-MM-DDThh-mm-ss) in Query Timezone
            let projectAsOfDate = this.getProjectAsOfDate(queryTimeZone);

            // force all three dates to be in browser timezone before doing isSameOrBefore check.
            // this is allowed because we are only interested in comparing `dates`. Thus, it does
            // not matter if the dates are in a certain timezone, they just have to be all in
            projectAsOfDate = moment(projectAsOfDate.format(CALENDAR_DATE_FORMAT));
            fromDate = moment(fromDate.format(CALENDAR_DATE_FORMAT));
            toDate = moment(toDate.format(CALENDAR_DATE_FORMAT));

            const isSameOrBeforeAsOfDate =
              fromDate.isSameOrBefore(projectAsOfDate, `minute`) && toDate.isSameOrBefore(projectAsOfDate, `minute`);
            const datesCorrectlyOrdered = fromDate.isSameOrBefore(toDate, `day`);
            if (
              selectedDateRange.getCalendarMaxDate() === NO_MAX_DATE_VALUE ||
              (isSameOrBeforeAsOfDate && datesCorrectlyOrdered && this.helpers.shouldShowQueryDaysLimit() === false)
            ) {
              return true;
            }
          }
          return false;
        },
        handleApplyClick: (): void => {
          if (this.helpers.canApply()) {
            let dateRange = this.getSelectedDateRange(this.state.pickerMode);
            dateRange = this.handleMidnightExactMinuteDateRange(dateRange);

            this.dispatchEvent(
              new CustomEvent(`apply`, {
                detail: {
                  dateRange,
                  fromTimeText: formatTimeString(this.state.fromTimeText),
                  isTimePickerEnabled: this.state.isTimePickerEnabled,
                  toTimeText: formatTimeString(this.state.toTimeText),
                },
              }),
            );

            this.track(`[mp-query-builder] Select custom date range`, {
              ...this.getDateRangeTrackingProps(dateRange),
              'Ending days ago (offset)':
                getDatePickerMode(dateRange) === DatePickerMode.InTheLast
                  ? getRelativeOffset(dateRange, this.getProjectAsOfDate())?.value
                  : null,
            });
          }
        },
        renderCalendarDayTooltip: (day: CalendarDay): null | VNode => {
          if (this.projectAsOfDateDisabledTimestamps.size > 0) {
            const dayTimestamp = moment({
              year: day.year,
              month: day.month,
              day: day.isoDayOfMonth,
            }).valueOf();
            if (this.projectAsOfDateDisabledTimestamps.has(dayTimestamp)) {
              const superimportMessage = `Data last imported ${this.getProjectAsOfDate().format(
                SHORT_MONTH_CALENDAR_DATE_FORMAT,
              )}`;
              return h(`mp-tooltip`, {attrs: {elref: `dateTooltip`, placement: `top`}}, superimportMessage);
            }
          }
          return null;
        },
        getTimeRangesTooltipText: () => {
          if (this.attr(`disable-sub-day-granularity-message`)) {
            return this.attr(`disable-sub-day-granularity-message`);
          } else if (this.state.isTimePickerEnabled) {
            return this.t(`reports.date-range.custom.advanced.disable-time-ranges`);
          } else {
            return this.t(`reports.date-range.custom.advanced.enable-time-ranges`);
          }
        },
        getRelativeInputMax: (unit: TimeUnit): number => {
          if (unit === TimeUnit.Hour) {
            return LAST_HOURS_MAX;
          }
          if (unit === TimeUnit.Minute) {
            return LAST_MINUTES_MAX;
          }
          return null;
        },
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  get print() {
    return this.getContext(`i18n`).print;
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    switch (name) {
      case `date-range`: {
        const dateRangeAttrs: DateRangeAttributes = JSON.parse(newValue);

        if (DateRange.hasSubDayGranularity(dateRangeAttrs)) {
          this.update({
            fromTimeText: [DateRangeType.Between, DateRangeType.Since].includes(dateRangeAttrs.type)
              ? getTimeString(moment(String(dateRangeAttrs.from), TIMESTAMP_FORMAT))
              : ``,
            isTimePickerEnabled: true,
            toTimeText:
              DateRangeType.Since === dateRangeAttrs.type
                ? getTimeString(moment(String(dateRangeAttrs.to), TIMESTAMP_FORMAT))
                : ``,
          });
        }

        const dateRangeClass = this.getDateRangeClass();
        const dateRange = dateRangeAttrs && new dateRangeClass(dateRangeAttrs);
        if (dateRange) {
          if (!dateRange.isValid()) {
            throw new Error(`"date-range" attribute contains invalid DateRange info`);
          }
          switch (dateRange.type) {
            case DateRangeType.On:
            case DateRangeType.RelativeAfter:
            case DateRangeType.Between:
            case DateRangeType.Since:
              break;
            default:
              throw new Error(`"date-range" attribute contains DateRange info with unsupported DateRangeType`);
          }

          const pickerMode = getDatePickerMode(dateRange);
          this.setSelectedDateRange(pickerMode, dateRange.getAttributes());
          this.state.pickerMode = pickerMode;

          // super.attributeChangedCallback() naturally calls this.update();
          const selectedDateRange = this.getSelectedDateRange(pickerMode);
          Object.assign(this.state, this.getInputStateFromDateRange(selectedDateRange));
        }
        // super.attributeChangedCallback() naturally calls this.update();
        this.state.selectedPresetIdx = this.getSelectedPresetIdx();
        if (dateRange) {
          this.state.showCustomControls = this.state.selectedPresetIdx === -1;
        }
        break;
      }
      case `max-history-days`: {
        // super.attributeChangedCallback() naturally calls this.update();
        // update selectedPresetIdx here because preset options may change depending on max-history-days
        this.state.selectedPresetIdx = this.getSelectedPresetIdx();
        break;
      }
      case `display-context`: {
        const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
        if (selectedDateRange) {
          // super.attributeChangedCallback() naturally calls this.update();
          Object.assign(this.state, this.getInputStateFromDateRange(selectedDateRange));
        }

        // super.attributeChangedCallback() naturally calls this.update();
        // update selectedPresetIdx here because preset options may change depending on display-context
        this.state.selectedPresetIdx = this.getSelectedPresetIdx();
        break;
      }
    }

    super.attributeChangedCallback(name, oldValue, newValue);

    // run after super.attributeChangedCallback() because "this.getProjectAsOfDate()" relies on this.attr()
    if (name === `project-as-of-date`) {
      this.projectAsOfDateDisabledTimestamps.clear();
      const today = getCurrentMoment({localized: true});
      for (let date = this.getProjectAsOfDate().add(1, `day`); date.isSameOrBefore(today, `day`); date.add(1, `day`)) {
        this.projectAsOfDateDisabledTimestamps.add(date.valueOf());
      }
    }
  }

  public connectedCallback() {
    super.connectedCallback();

    this.update({
      fromTimeText:
        this.state.fromTimeText === MIDNIGHT_TIME_STRING && this.attr(`from-time-text`) !== ``
          ? this.attr(`from-time-text`)
          : this.state.fromTimeText,
      isTimePickerEnabled:
        this.attr(`disable-sub-day-granularity-message`) === `` &&
        (this.attr(`is-time-picker-enabled`) || this.state.isTimePickerEnabled),
      toTimeText:
        this.state.toTimeText === MINUTE_BEFORE_MIDNIGHT_TIME_STRING && this.attr(`to-time-text`) !== ``
          ? this.attr(`to-time-text`)
          : this.state.toTimeText,
    });
  }

  public disconnectedCallback() {
    this.cleanupMouseupOrNewFocusListener();
    super.disconnectedCallback();
  }

  public customScrollIntoViewIfNeeded() {
    requestAnimationFrame(() => {
      scrollIntoViewIfNeeded(this);
    });
  }

  // Expose these handlers publicly so they can be used directly from tests,
  // important because focus/blur workflows are difficult to test reliably.
  public handleFromDateFocus(): void {
    this.fromDateInputRecentlySelected = true;
  }
  public handleFromDateBlur(): void {
    this.deferUntilFrameAfterMouseupOrNewFocus(() => (this.fromDateInputRecentlySelected = false));
    this.applyAndFormatFromAndToDateInputs(this.state);
  }
  public handleFromTimeBlur(): void {
    const fromTime: moment.Moment = parseTime(this.state.fromTimeText);
    if (fromTime.isValid()) {
      this.update({
        fromTimeText: getTimeString(fromTime),
      });
    }
  }
  public handleToDateFocus(): void {
    this.toDateInputRecentlySelected = true;
  }
  public handleToDateBlur(): void {
    this.deferUntilFrameAfterMouseupOrNewFocus(() => (this.toDateInputRecentlySelected = false));
    this.applyAndFormatFromAndToDateInputs(this.state);
  }
  public handleToTimeBlur(): void {
    const toTime: moment.Moment = parseTime(this.state.toTimeText);
    if (toTime.isValid()) {
      this.update({
        toTimeText: getTimeString(toTime),
      });
    }
  }

  private getTimeOrDefault(timeText: string, defaultTime: moment.Moment = ZERO_TIME): moment.Moment {
    const parsedTime = parseTime(timeText);
    return parsedTime.isValid() ? parsedTime : defaultTime;
  }

  /**
   * This function exists to serve an extremely specific edge case and should not
   * be used for any purpose outside of that edge case. Inside the domain of that
   * edge case it should still be used VERY sparingly.
   *
   * This function handles the case where a user has selected identical from date
   * and to dates at midnight on some day. Because our date ranges are inclusive,
   * normally a timestamp like '2024-04-06T00:00:00', for the 'to' part of a date
   * range it means the entire day. This is because we treat date ranges as inclusive
   * and we treat this timestamp as referring to 2024-04-06 essentially, instead of
   * midnight specifically on 2024-04-06. However this means that without intervention
   * a customer cannot look at the exact minute of midnight on a single day, e.g.
   * from: 2024-07-15T00:00:00, to: 2024-07-15T00:00:00, even when the time picker is
   * enabled.
   *
   * To solve that problem this function checks for those very specific conditions and if
   * they are met this function adds 59 seconds to the 'to' timestamp.
   *
   * If those conditions are not met this function checks if the passed in DateRange has a
   * valid timestamp in the 'to' field (as opposed to a RelativeDate) and if so clears
   * seconds from the 'to' timestamp to undo any work previously done by this function.
   *
   * If the 'to' field is not a timestamp string, then the original DateRange is returned
   * unchanged.
   *
   * @param dateRange the date range to check for the highly specific conditions.
   * @returns the passed in date range with 59 seconds added to it if the highly specific
   *          conditions are met.
   */
  private handleMidnightExactMinuteDateRange(dateRange: DateRange): DateRange {
    const dateRangeClass = this.getDateRangeClass();
    const toTimestamp = parseTimestamp(dateRange.to as string);
    const toTime = getTime(toTimestamp);
    let dateRangeAttrs;
    if (
      this.state.isTimePickerEnabled &&
      dateRange.type === DateRangeType.Between &&
      dateRange.from === dateRange.to &&
      toTimestamp.isValid() &&
      isTimeZero(toTime)
    ) {
      dateRangeAttrs = {
        ...dateRange,
        to: toTimestamp.add(59, `second`).format(TIMESTAMP_FORMAT),
      };
      return new dateRangeClass(dateRangeAttrs);
    } else if (this.state.isTimePickerEnabled && dateRange.type === DateRangeType.Between && toTimestamp.isValid()) {
      dateRangeAttrs = {
        ...dateRange,
        to: toTimestamp.startOf(`minute`).format(TIMESTAMP_FORMAT),
      };

      return new dateRangeClass(dateRangeAttrs);
    }
    return dateRange;
  }

  private isTimeRangeValid(): boolean {
    if (!this.state.isTimePickerEnabled) {
      return true;
    }
    switch (this.state.pickerMode) {
      case DatePickerMode.Between:
        return parseTime(this.state.fromTimeText).isValid() && parseTime(this.state.toTimeText).isValid();
      case DatePickerMode.InTheLast:
        return true;
      case DatePickerMode.On:
      case DatePickerMode.Since:
        return parseTime(this.state.fromTimeText).isValid();
    }
  }

  private getCurrentProject(): CurrentProject | undefined {
    return this.getContext(`currentProject`).get().value;
  }

  private getProjectAsOfDate(timezone?: Timezone): moment.Moment {
    const explicitAsOfDate = this.attr(`project-as-of-date`);
    if (explicitAsOfDate) {
      const parsedDate = parseDate(explicitAsOfDate);
      if (!parsedDate) {
        throw new Error(`"project-as-of-date" attribute does not contain a valid date string`);
      }
      return moment(parsedDate);
    }
    return getCurrentMoment({localized: true, timezone});
  }

  private getInTheLastWindowErrorMessage(value: number, unit: TimeUnit): string {
    if (Number.isNaN(value)) {
      return `Input must be an integer`;
    }
    if (value < 0) {
      return this.t(`reports.date-range.limits.last-range-min`, {
        min: 0,
        unit,
      });
    }

    const max: number = this.helpers.getRelativeInputMax(unit);
    if (max && value > max) {
      return this.t(`reports.date-range.limits.last-range-max`, {
        max,
        unit,
      });
    }
    return ``;
  }

  private setSelectedDateRange(pickerMode: DatePickerMode, dateRange: DateRangeAttributes | null) {
    this.selectedDateRanges.set(pickerMode, dateRange);
  }

  private getSelectedDateRange(pickerMode: DatePickerMode): null | DateRange {
    if (this.selectedDateRanges.has(pickerMode)) {
      const dateRangeAttrs = this.selectedDateRanges.get(pickerMode);
      if (dateRangeAttrs) {
        const dateRangeClass = this.getDateRangeClass();
        return new dateRangeClass(this.selectedDateRanges.get(pickerMode));
      }
    }
    return null;
  }

  private getSelectedPresetIdx(): number {
    const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
    if (selectedDateRange) {
      const presetMenuItems = this.helpers.getPresetMenuSections()[0].items;
      return presetMenuItems.findIndex((menuItem) => selectedDateRange.equals(menuItem.value));
    }
    return -1;
  }

  private getDateRangeClass(): typeof DateRange {
    const displayContext = this.attr(`display-context`);
    return DATE_RANGE_TYPES[displayContext];
  }

  private getDateRangeFromInputs(
    pickerMode: DatePickerMode,
    inputState: Partial<DatePickerInputState>,
  ): DateRangeAttributes | null {
    const {
      fromDate,
      fromTimeText,
      isTimePickerEnabled,
      toDate,
      toTimeText,
      relativeWindowValue,
      relativeWindowUnit,
      relativeOffsetValue,
    } = inputState;
    switch (pickerMode) {
      case DatePickerMode.InTheLast: {
        const windowValue: number = Number.isInteger(relativeWindowValue) ? relativeWindowValue : 0;
        const max: number = this.helpers.getRelativeInputMax(relativeWindowUnit);
        if (windowValue >= 0 && (!max || windowValue <= max)) {
          const window: RelativeDate = {
            value: Number(windowValue),
            unit: relativeWindowUnit,
          };

          const offsetValue = Number.isInteger(relativeOffsetValue) ? relativeOffsetValue : 0;
          if (offsetValue >= 0) {
            const canBeRolling = window.unit === TimeUnit.Day;
            const offset = canBeRolling
              ? {
                  value: relativeOffsetValue,
                  unit: window.unit,
                }
              : null;

            return getInTheLastDateRangeAttributes(window, offset);
          }
        }
        return null;
      }
      case DatePickerMode.On: {
        if (fromDate?.isValid()) {
          return getOnDateRangeAttributes(fromDate);
        }
        break;
      }
      case DatePickerMode.Between: {
        if (fromDate?.isValid() && toDate?.isValid()) {
          const time = isTimePickerEnabled
            ? {
                fromTime: parseTime(fromTimeText),
                toTime: parseTime(toTimeText),
              }
            : null;
          return getBetweenDateRangeAttributes(fromDate, toDate, time);
        }
        break;
      }
      case DatePickerMode.Since: {
        if (fromDate?.isValid()) {
          return getSinceDateRangeAttributes(fromDate);
        }
        break;
      }
    }
    return null;
  }

  private getInputStateFromDateRange(dateRange: DateRange): null | Partial<DatePickerInputState> {
    const pickerMode = getDatePickerMode(dateRange);
    switch (pickerMode) {
      case DatePickerMode.InTheLast: {
        const relativeOffsetValue = getRelativeOffset(dateRange, this.getProjectAsOfDate())?.value;
        return {
          relativeWindowValue: getRelativeWindowValue(dateRange, this.getProjectAsOfDate()),
          relativeWindowUnit: getRelativeWindowUnit(dateRange),
          relativeOffsetValue,

          // note: if relativeOffsetValue is explicitly zero, don't change showEndingField
          ...(relativeOffsetValue === 0 ? {} : {showEndingField: relativeOffsetValue > 0}),
        };
      }
      case DatePickerMode.On:
      case DatePickerMode.Between:
      case DatePickerMode.Since: {
        const selectedFromTimestamp = parseTimestamp(dateRange.getCalendarFromDate());
        const selectedToTimestamp = parseTimestamp(dateRange.getCalendarToDate());

        const defaultFromTimestamp = selectedFromTimestamp || this.state.fromDate;
        const defaultToTimestamp = selectedToTimestamp || this.state.toDate;

        // Preserve the fromTimeText across time picker enabled/disabled.
        const fromTimeText = !this.state.isTimePickerEnabled
          ? this.state.fromTimeText
          : getTimeString(defaultFromTimestamp);
        const toTimeText =
          !this.state.isTimePickerEnabled || !defaultToTimestamp?.isValid()
            ? this.state.toTimeText
            : getTimeString(defaultToTimestamp);

        return {
          fromDateText: defaultFromTimestamp ? formatDate(defaultFromTimestamp.toDate()) : ``,
          fromDate: defaultFromTimestamp,
          fromTimeText,
          toDateText: defaultToTimestamp ? formatDate(defaultToTimestamp.toDate()) : ``,
          toDate: defaultToTimestamp,
          toTimeText,
        };
      }
      default: {
        const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
        throw new Error(`unknown picker mode ${unreachable}`);
      }
    }
  }

  private shouldShowCalendarPreviewSelection(pickerMode: DatePickerMode): boolean {
    switch (pickerMode) {
      case DatePickerMode.InTheLast:
      case DatePickerMode.Since: {
        // if there is an existing selection, just don't show the preview selection to avoid visual overlapping
        return !this.getSelectedDateRange(pickerMode);
      }
      case DatePickerMode.Between: {
        const {fromDate, toDate} = this.state;
        const onlyOneDate = (!fromDate && toDate) || (!toDate && fromDate);
        return !!onlyOneDate;
      }
      default:
        return false;
    }
  }

  /**
   * Given a DatePickerMode and a date return the date range for the date being hovered over.
   *
   * @param {DatePickerMode} pickerMode the picked mode to get the hover date range for.
   * @param {moment.Moment} hoverDate the date currently being selected or hovered over.
   * @returns
   */
  private getCalendarHoverDateRange(pickerMode: DatePickerMode, hoverDate: null | moment.Moment): null | DateRange {
    const dateRangeClass = this.getDateRangeClass();
    switch (pickerMode) {
      case DatePickerMode.InTheLast: {
        if (hoverDate) {
          const queryTimeZone = this.getCurrentProject()?.timezone;
          const dayValue = this.getProjectAsOfDate(queryTimeZone).diff(hoverDate, TimeUnit.Day);
          const offsetValue = Number.isInteger(this.state.relativeOffsetValue) ? this.state.relativeOffsetValue : 0;
          const windowValue = dayValue - offsetValue;
          return new dateRangeClass(
            getInTheLastDateRangeAttributes(
              {value: windowValue >= 0 ? windowValue : dayValue, unit: TimeUnit.Day},
              {value: windowValue >= 0 ? offsetValue : 0, unit: TimeUnit.Day},
            ),
          );
        }
        break;
      }
      case DatePickerMode.Between: {
        const {fromDate, toDate} = this.state;
        const hoverAsOfDate = (!fromDate && toDate) || (!toDate && fromDate);
        if (hoverAsOfDate) {
          return new dateRangeClass(getBetweenDateRangeAttributes(hoverDate || hoverAsOfDate, hoverAsOfDate));
        }
        break;
      }
      case DatePickerMode.On: {
        if (hoverDate) {
          return new dateRangeClass(getOnDateRangeAttributes(hoverDate));
        }
        break;
      }
      case DatePickerMode.Since: {
        if (hoverDate) {
          return new dateRangeClass(getSinceDateRangeAttributes(hoverDate));
        }
        break;
      }
    }
    return null;
  }

  private applyAndFormatFromAndToDateInputs(inputState: Partial<DatePickerInputState>): void {
    const replacement = this.getDateRangeFromInputs(this.state.pickerMode, inputState);
    this.setSelectedDateRange(this.state.pickerMode, replacement);

    const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
    if (selectedDateRange) {
      const newInputState = this.getInputStateFromDateRange(selectedDateRange);
      this.update({
        ...newInputState,
        ...this.getDateRangeFromInputs(this.state.pickerMode, newInputState),
      });
    } else {
      this.update();
    }
  }

  private track(event: string, properties: {[index: string]: any}): void {
    emitTrackEvent(this, {eventName: event, props: properties, cancelable: true});
  }

  private getDateRangeTrackingProps(dateRange: DateRange) {
    return generateDateRangeTrackingProps(
      dateRange,
      getReportName(),
      this.state.pickerMode,
      this.getProjectAsOfDate(),
      this.attr(`tracked-use-case`),
    );
  }

  private cleanupMouseupOrNewFocusListener(): void {
    if (this.mouseupOrNewFocusListener) {
      window.removeEventListener(`mouseup`, this.mouseupOrNewFocusListener);
      window.removeEventListener(`touchend`, this.mouseupOrNewFocusListener);
      this.el.removeEventListener(`focusin`, this.mouseupOrNewFocusListener, true);
    }
    this.mouseupOrNewFocusListener = null;
  }

  private deferUntilFrameAfterMouseupOrNewFocus(deferredAction: () => void): void {
    this.cleanupMouseupOrNewFocusListener();

    this.mouseupOrNewFocusListener = () => {
      this.cleanupMouseupOrNewFocusListener();
      requestAnimationFrame(deferredAction.bind(this));
    };
    window.addEventListener(`mouseup`, this.mouseupOrNewFocusListener);
    window.addEventListener(`touchend`, this.mouseupOrNewFocusListener);
    this.el.addEventListener(`focusin`, this.mouseupOrNewFocusListener, true);
  }

  public canUseTimePicker(): boolean {
    // Time picker is disabled for legacy reports, like experiments and impact.
    return !LegacyReports.includes(getReportName());
  }
}

type JsxProps = {
  attrs?: Partial<DateRangePickerAttrs>;
  on?: {
    apply?: (ev: DateRangePickerApplyEvent) => void;
    cancel?: () => void;
  };
};

export const DateRangePickerEl = defineAndMakeJsxFactory<JsxProps>(`mp-date-range-picker-new`, DateRangePicker);
export const DateRangePickerReact = panel2react<JsxProps>(`mp-date-range-picker-new`, DateRangePicker);
